import {
  type AdaptationMode,
  clampKnownShare,
  projectionFor,
} from "~/utils/demandPreorderDevelopment"

/**
 * Fashion pre-order development. The accepted comparables' season pre-orders are the expected
 * total; the collection's pre-order curve (`PreorderFactorsCollection`) gives the share normally
 * booked by now, and the pace is known-now against that share of the total (legacy
 * `preOrderPaceTrendDecimal`). The four answers keep their NOOS meaning with "last year" replaced
 * by "the comparable variants".
 *
 * Pure, so the server read, the row figure and the chart share one arithmetic.
 */

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

export type PreorderCurvePoint = {
  /** Weeks from the collection's `startDate`; negative before it. The column name is misleading. */
  weeksBeforeCollectionStart: number
  fractionKnown: number
}

/** Predecessor's curve, else `global`, else none (legacy `_attach_collection_season_key`). */
export const GLOBAL_PREORDER_CURVE_KEY = "global"

export const preorderCurveKeyFor = (
  predecessorKey: string | null,
  availableKeys: ReadonlySet<string>,
): string | null => {
  if (predecessorKey != null && availableKeys.has(predecessorKey)) return predecessorKey
  return availableKeys.has(GLOBAL_PREORDER_CURVE_KEY) ? GLOBAL_PREORDER_CURVE_KEY : null
}

/** Backward as-of join: latest point at or before `weeks`, else 0. Points need not be sorted. */
export const fractionKnownAt = (curve: readonly PreorderCurvePoint[], weeks: number): number => {
  let best: PreorderCurvePoint | null = null
  for (const point of curve) {
    if (point.weeksBeforeCollectionStart > weeks) continue
    if (best == null || point.weeksBeforeCollectionStart > best.weeksBeforeCollectionStart)
      best = point
  }
  return best == null ? 0 : Math.min(Math.max(best.fractionKnown, 0), 1)
}

/** Below this share known, the pace is noise (legacy `MIN_KNOWN_PCT_FOR_PREORDER_TREND`). */
const MIN_KNOWN_SHARE_FOR_PACE = 0.05

export type FashionPreorderVariantRow = {
  variantId: string
  /** The accepted comparables' season pre-orders, folded as the baseline folds their sales. */
  comparables: number
  /**
   * The DELIVERED part of `comparables`, i.e. the pre-orders inside this variant's baseline. The
   * engine subtracts this, not `comparables`, which would remove units never delivered.
   */
  baselinePreorders: number
  known: number
  fractionKnownNow: number
}

export type FashionPreorderDevelopmentFigures = {
  totals: {
    comparables: number
    baselinePreorders: number
    /** Σ `comparables × fractionKnownNow`. */
    expectedKnownNow: number
    knownNow: number
  }
  /** The override if set, else `measuredKnownShare`; `expectedKnownNow` is built from it. */
  knownShare: number
  /** The curve's share, comparables-weighted; kept even under an override. */
  measuredKnownShare: number
  overrideShare: number | null
  /** Below `MIN_KNOWN_SHARE_FOR_PACE` the adapting answers fall back to `maintain`. */
  paceReadable: boolean
  /** `knownNow − expectedKnownNow`, stated even when unreadable. */
  paceDiff: number
  /** Null when unreadable or nothing was expected yet. */
  pacePct: number | null
  /** Σ of each variant's own projection (`projectVariant`), never the pooled book's. */
  projected: number
  /** `projected` under every answer, for the option previews. */
  projectedByMode: Record<AdaptationMode, number>
  /**
   * `projectedByMode` on the measured share, ignoring the override: sizes the pinned axis, which
   * must not move when the planner drags the share under an answer the share doesn't change.
   */
  measuredProjectedByMode: Record<AdaptationMode, number>
  /** `projected − knownNow`; never negative, since each projection is floored at its own book. */
  development: number
  vsComparables: number
  vsComparablesPct: number | null
  /**
   * What the answer adds to the plan (`projected − baselinePreorders`); the figure the engine
   * applies. Equals `vsComparables` when the comparable season delivered its whole book.
   */
  vsBaseline: number
  vsBaselinePct: number | null
}

const NO_TOTALS = { comparables: 0, baselinePreorders: 0, expectedKnownNow: 0, knownNow: 0 }

/** Until the planner picks: "Maintain expectations". */
export const DEFAULT_FASHION_PREORDER_MODE: AdaptationMode = "adaptChange"

/**
 * An answer's season projection, floored at the confirmed book (in practice only `maintain` can
 * go below). Shared by the option previews and the chart axis so they cannot disagree.
 */
export const fashionProjectionFor = (
  mode: AdaptationMode,
  totals: { comparables: number; knownNow: number },
  paceDiff: number,
  pacePct: number | null,
): number =>
  Math.max(
    projectionFor(mode, totals.comparables, totals.knownNow, paceDiff, pacePct),
    totals.knownNow,
  )

const ADAPTATION_MODES: readonly AdaptationMode[] = [
  "closeBook",
  "maintain",
  "adaptChange",
  "adaptTrend",
]

/**
 * One variant's season on its OWN comparables, book and pace, floored at its own confirmed book.
 * The plan figure is the sum of these, so a style booking ahead keeps its own orders instead of
 * sharing them with the rest by comparables weight.
 */
const projectVariant = (
  row: FashionPreorderVariantRow,
  mode: AdaptationMode,
  overrideShare: number | null,
): number => {
  const share = overrideShare == null ? row.fractionKnownNow : clampKnownShare(overrideShare)
  const expected = row.comparables * share
  const readable = share >= MIN_KNOWN_SHARE_FOR_PACE && expected > 0
  const diff = row.known - expected
  return fashionProjectionFor(
    mode,
    { comparables: row.comparables, knownNow: row.known },
    readable ? diff : 0,
    readable ? diff / expected : null,
  )
}

const projectRows = (
  rows: readonly FashionPreorderVariantRow[],
  mode: AdaptationMode,
  overrideShare: number | null,
): number => rows.reduce((sum, row) => sum + projectVariant(row, mode, overrideShare), 0)

export const computeFashionPreorderDevelopment = (
  rows: readonly FashionPreorderVariantRow[] | undefined,
  mode: AdaptationMode,
  /** The planner's "share booked by now", 0–1; replaces the curve's share, not the pre-orders. */
  overrideShare: number | null = null,
): FashionPreorderDevelopmentFigures => {
  const measured = (rows ?? []).reduce(
    (acc, row) => ({
      comparables: acc.comparables + row.comparables,
      baselinePreorders: acc.baselinePreorders + row.baselinePreorders,
      expectedKnownNow: acc.expectedKnownNow + row.comparables * row.fractionKnownNow,
      knownNow: acc.knownNow + row.known,
    }),
    NO_TOTALS,
  )
  const measuredKnownShare =
    measured.comparables > 0 ? measured.expectedKnownNow / measured.comparables : 0
  const effectiveShare = overrideShare == null ? measuredKnownShare : clampKnownShare(overrideShare)
  const totals =
    overrideShare == null
      ? measured
      : { ...measured, expectedKnownNow: measured.comparables * effectiveShare }
  const knownShare = effectiveShare
  const paceReadable = knownShare >= MIN_KNOWN_SHARE_FOR_PACE && totals.expectedKnownNow > 0
  const paceDiff = totals.knownNow - totals.expectedKnownNow
  const pacePct = paceReadable ? paceDiff / totals.expectedKnownNow : null
  const all = rows ?? []
  const byMode = (share: number | null) =>
    Object.fromEntries(ADAPTATION_MODES.map((m) => [m, projectRows(all, m, share)])) as Record<
      AdaptationMode,
      number
    >
  const projectedByMode = byMode(overrideShare)
  const measuredProjectedByMode = overrideShare == null ? projectedByMode : byMode(null)
  const projected = projectedByMode[mode]
  return {
    totals,
    knownShare,
    measuredKnownShare,
    overrideShare: overrideShare == null ? null : clampKnownShare(overrideShare),
    paceReadable,
    paceDiff,
    pacePct,
    projected,
    projectedByMode,
    measuredProjectedByMode,
    development: projected - totals.knownNow,
    vsComparables: projected - totals.comparables,
    vsComparablesPct:
      totals.comparables > 0 ? (projected - totals.comparables) / totals.comparables : null,
    vsBaseline: projected - totals.baselinePreorders,
    vsBaselinePct:
      totals.baselinePreorders > 0
        ? (projected - totals.baselinePreorders) / totals.baselinePreorders
        : null,
  }
}

/** What the answer adds to one variant: its own projection minus its own `baselinePreorders`. */
export const fashionPreorderUnitsFor = (
  row: FashionPreorderVariantRow,
  mode: AdaptationMode,
  overrideShare: number | null = null,
): number => projectVariant(row, mode, overrideShare) - row.baselinePreorders

export type FashionPreorderCollection = {
  collectionId: number
  name: string
  /** ISO `YYYY-MM-DD` `Collection.startDate`. */
  startDate: string | null
  curveKey: string | null
  /** Whole weeks from `startDate` to the planning week, truncated toward zero as legacy does. */
  weeksFromStart: number | null
  fractionKnownNow: number
  curve: PreorderCurvePoint[]
}

/**
 * The route's expected-known share per week, each collection's curve weighted by its comparables'
 * pre-orders. Every collection is evaluated on the shared calendar of all drops (holding its last
 * share past its curve's end), or the line dips as a drop runs out of points. No `startDate` ⇒ left
 * out.
 */
export const paceCurveFor = (
  collections: readonly FashionPreorderCollection[],
  comparablesByCollection: ReadonlyMap<number, number>,
): { weekStart: string; fractionKnown: number }[] => {
  const total = [...comparablesByCollection.values()].reduce((sum, v) => sum + v, 0)
  if (total <= 0) return []
  const drawn = collections.filter(
    (c) =>
      (comparablesByCollection.get(c.collectionId) ?? 0) > 0 &&
      c.startDate != null &&
      c.curve.length > 0,
  )
  if (drawn.length === 0) return []

  const weeks = new Set<number>()
  for (const collection of drawn) {
    const startMs = Date.parse(collection.startDate!)
    const offsets = collection.curve.map((p) => p.weeksBeforeCollectionStart)
    for (let week = Math.min(...offsets); week <= Math.max(...offsets); week++)
      weeks.add(startMs + week * WEEK_MS)
  }

  return [...weeks]
    .sort((a, b) => a - b)
    .map((ms) => ({
      weekStart: new Date(ms).toISOString().slice(0, 10),
      fractionKnown: drawn.reduce((sum, collection) => {
        const weight = (comparablesByCollection.get(collection.collectionId) ?? 0) / total
        const week = Math.round((ms - Date.parse(collection.startDate!)) / WEEK_MS)
        return sum + weight * fractionKnownAt(collection.curve, week)
      }, 0),
    }))
}
